import React, {
  useEffect, useRef, useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';
import uniqid from 'uniqid';

import { store, useStore } from 'polaris-coreweb/exports';
import * as GooglePublisherTag from 'SharedHelpers/googlePublisherTag';

import { withPolaris } from 'Components/protons/Polaris';
import { withSticky } from 'Components/protons/withSticky';

import { Link } from 'Components/molecules/Link';

import { withIAS } from './withIAS';
import withErrorBoundary from './withErrorBoundary';

import './_style.scss';

/**
 * Boolean to check if the first run. All ad components will reference this variable.
 * https://stackoverflow.com/questions/57444154/why-need-useref-and-not-mutable-variable
 * @type {boolean}
 */
let firstRun = true;

// Slot timeouts.
const slotTimeouts = [];

/**
 * New Ad component
 *
 * NOTES:
 * https://developers.google.com/publisher-tag/guides/ad-best-practices
 *
 * @param {string} props.id — A string to be used as container div id.
 * @param {string} props.label — String to be displayed on top of the ad container
 * @param {string} props.adUnitPath — An string indicating ad unit path, which will be used to create an ad slot.
 * @param {array} props.slotSize — Prop to specify the ad slot size.This will be preceded by the sizeMapping if specified
 * @param {array} props.sizeMapping — An optional array of object, which contains an array of viewport size and slot size.
 *                              This needs to be set if the ad needs to serve different ad sizes per different viewport sizes (responsive ad).
 *                              Setting the slot to any dimension that's not configured in DFP results in rendering an empty ad.
 *                              The ad slot size, which is provided for the viewport size of [0, 0] will be used as default ad size if none of viewport size matches.
 * @param {boolean} props.loadStyles — Whether to apply the class names for styling.
 * @param {Object} props.extraClassNames — Extra class names to be applied to the component
 * @param {Object} props.targeting — An optional object, which includes ad targeting key-value pairs
 * @param {boolean} props.isFullWidth — Ad takes full screen width
 * @param {boolean} props.isMobileSticky — Ad is set at the bottom of the screen (visible on mobile only)
 * @param {boolean} props.isSidebar — Use for the ad placed in the sidebar/aside; background will be changed to white and ad container will have border outline in colour of default ad background
 * @param {boolean} props.isSpaceReserved — Use to reserve space for ad (to avoid shifting of content after ad @link https://web.dev/cls/)
 * @param {boolean} props.isNoBackground — Default background is removed
 * @param {boolean} props.isNative — Will set -native-ad selector
 * @param {boolean} props.isSkippable — use for accessibility via adding skip link for tab navigation though the site-app
 * @param {boolean} props.isRightSkip — if ad skip btn need to be hidden from right side
 * @param {boolean} props.collapseEmptyDiv — Collapse empty div
 * @param {Object} props.context — Extra context to be passed to the component
 * @param {string} props.type — The slot type.
 * @param {string} props.targetViewport — From inline ads, defines whether it's a mobile or desktop ad
 * @param {boolean} props.outOfPage — Out of page ad
 * @param {boolean} props.isPageImpression — Page impression ad
 * @param {boolean} props.iasPubId — IAS pub id
 *
 * @returns {JSX.Element} - Ad component
 */
const Ad = (props) => {
  const {
    id,
    adUnitPath,
    label,
    slotSize,
    sizeMapping,
    loadStyles = true,
    extraClassNames,
    isFullWidth,
    isMobileSticky,
    isSidebar,
    isSpaceReserved,
    isNoBackground,
    isNative,
    isSkippable,
    isRightSkip,
    context: {
      cssPrefix, adConfig, globalSettings, breakpoints,
    },
    targeting: localTargeting,
    collapseEmptyDiv,
    type,
    targetViewport,
    outOfPage,
    isPageImpression,
    iasPubId,
  } = props;

  // Hold up the creation of ad slots until we're sure all the scripts are ready.
  const [startAds, setStartAds] = useState(null);

  const checkScriptsReady = async () => {
    const isOzoneReady = () => new Promise((resolve) => {
      if (typeof window.ozoneFetchBids === 'function') {
        resolve(true);
      } else {
        resolve(false);
      }
    });

    const isApstagReady = () => new Promise((resolve) => {
      if (window.apstagLOADED) {
        resolve(true);
      } else {
        resolve(false);
      }
    });

    const results = await Promise.all([isOzoneReady(), isApstagReady()]);
    return results.every((ready) => ready);
  };

  // Check if the scripts are ready and set the state accordingly.
  useEffect(() => {
    const maxRetries = 5;
    const interval = 100;
    let retries = 0;

    const checkInterval = setInterval(async () => {
      const ready = await checkScriptsReady();
      if (ready || retries >= maxRetries) {
        setStartAds(true);
        clearInterval(checkInterval);
      }
      retries += 1;
    }, interval);

    return () => clearInterval(checkInterval);
  }, []);

  // Set the viewport states for controlling whether the ad should be displayed.
  // The only slots that need to use this functionality are the inline ads, so only run it if the targetViewport is defined.
  const [divId, setDivId] = useState(id);
  const [activeViewport, setActiveViewport] = useState(null);
  useEffect(() => {
    let currentViewport = '';
    if (typeof window !== 'undefined' && targetViewport !== undefined) {
      const [vw] = GooglePublisherTag.getViewportDimensions();
      const { desktop } = breakpoints || {};
      if (desktop?.length) {
        currentViewport = vw < desktop[0]?.min ? 'mobile' : 'desktop';
        // If the viewports don't match (mobile ad on desktop or vice versa), unset the id.
        if (currentViewport !== targetViewport && divId !== '') {
          setDivId('');
        }
      }
      // If the component has rerendered and the viewport has changed, update the state.
      if (currentViewport !== activeViewport) {
        setActiveViewport(currentViewport);
        setDivId(id);
      }
    }
  });

  const mergedConfig = GooglePublisherTag.getAdConfig({
    adConfig,
    adUnitPath,
    slotSize,
    sizeMapping,
    type,
    localTargeting,
  });

  const {
    lineItemBlacklist,
    disableCoreWebAdRefresh,
    refreshTime,
  } = globalSettings?.adSettings || {};

  const {
    headerBidding,
    immediateLoadAdSlots,
  } = globalSettings || {};

  // Add the slot to the store if it doesn't exist.
  useEffect(() => {
    if (!Object.keys(store.getState()).includes(id)) {
      store.setState((prev) => ({
        ...prev,
        [id]: {
          visible: !!isSpaceReserved,
          rendered: false,
          displayed: false,
          timedOut: true,
          defined: false,
          initialFetched: false,
        },
      }));
    }
  }, []);

  // States from the store.
  const isVisible = useStore(useCallback((state) => state[id]?.visible, [id]));
  const isRendered = useStore(useCallback((state) => state[id]?.rendered, [id]));
  const isTimedOut = useStore(useCallback((state) => state[id]?.timedOut, [id]));
  const isDefined = useStore(useCallback((state) => state[id]?.defined, [id]));
  const isInitialFetched = useStore(useCallback((state) => state[id]?.initialFetched, [id]));
  const isDisplayed = useStore(useCallback((state) => state[id]?.displayed, [id]));

  /**
   * Callback for the slotRenderEnded pubads event.
   */
  const onSlotRenderEnded = (event) => {
    const { slot } = event || {};
    const slotId = slot?.getSlotElementId();
    if (slotId) {
      let visible = false;
      // Don't set as visible if 1x1 unit is being served or if the slot is empty.
      // "fluid" size is [0, 0], but the condition had to be split to width and height (size[0], size[1])
      if (
        event.isEmpty === false
        && (event.size[0] > 1 || (event.size[0] === 0 && event.size[1] === 0))
      ) {
        visible = true;
      }
      // Update the state.
      store.setState((prev) => ({
        ...prev,
        [slotId]: {
          ...prev[slotId],
          rendered: true,
          visible,
        },
      }));
      performance.mark(`slotRenderEnded--${slotId}`);
      const marks = performance.getEntriesByType('mark').map(mark => mark.name);
      if (marks.includes(`slotOnLoad--${slotId}`)) {
        performance.measure(
          `slot-onLoad-to-slotRenderEnded--${slotId}`,
          {
            detail: `${slotId}`,
            start: `slotOnLoad--${slotId}`,
            end: `slotRenderEnded--${slotId}`,
          },
        );
      }
    }
  };

  /**
   * Callback for slotOnload event.
   */
  const onSlotLoad = (event) => {
    const { slot } = event;
    const slotId = slot.getSlotElementId();
    if (slotId) {
      performance.mark(`slotOnLoad--${slotId}`);
      const measures = performance.getEntriesByType('measure').map(measure => measure.name);
      const marks = performance.getEntriesByType('mark').map(mark => mark.name);
      if (marks.includes('coreweb-start')) {
        // One off measurement to get time to first slot loaded and which slot it was.
        if (!measures.includes('coreweb-start-to-first-slotOnLoad')) {
          performance.mark(`first-slotOnLoad--${slotId}`);
          performance.measure(
            'coreweb-start-to-first-slotOnLoad',
            {
              detail: `${slotId}`,
              start: 'coreweb-start',
              end: `first-slotOnLoad--${slotId}`,
            },
          );
        }
        // If below_header ad, measure the time it took to load from coreweb-start.
        if (
          slotId === 'refresh-below_header'
          && !measures.includes('coreweb-start-to-below_header-slotOnLoad')
        ) {
          performance.mark(`${slotId}--slotOnLoad`);
          performance.measure(
            'coreweb-start-to-below_header-slotOnLoad',
            {
              detail: `${slotId}`,
              start: 'coreweb-start',
              end: `${slotId}--slotOnLoad`,
            },
          );
        }
      }
      // Measure the time the slot took to load after being requested.
      if (marks.includes(`slotRequested--${slotId}`)) {
        performance.measure(
          `slotRequested-to-slotOnLoad--${slotId}`,
          {
            detail: `${slotId}`,
            start: `slotRequested--${slotId}`,
            end: `slotOnLoad--${slotId}`,
          },
        );
      }
    }
  };

  /**
   * Callback for slot slotRequested event.
   */
  const onSlotRequested = (event) => {
    const { slot } = event || {};
    const slotId = slot?.getSlotElementId();
    if (slotId) {
      performance.mark(`slotRequested--${slotId}`);
      // One off measurement to get the time to first ad request and which slot called it.
      const measures = performance.getEntriesByType('measure').map(measure => measure.name);
      const marks = performance.getEntriesByType('mark').map(mark => mark.name);
      if (
        marks.includes('coreweb-start')
        && !measures.includes('coreweb-start-to-first-slotRequested')
      ) {
        performance.mark(`first-slotRequested--${slotId}`);
        performance.measure(
          'coreweb-start-to-first-slotRequested',
          {
            detail: `${slotId}`,
            start: 'coreweb-start',
            end: `first-slotRequested--${slotId}`,
          },
        );
      }
    }
  };

  /**
   * Callback for slot visibility change.
   * Visibility percentage can be found in event.inViewPercentage.
   */
  const onSlotVisibilityChanged = () => {};

  /**
   * Callback for slot slotResponseReceived event.
   */
  const onSlotResponseReceived = () => {};

  const onImpressionViewable = (event) => {
    const { slot } = event;
    const slotId = slot.getSlotElementId();

    const isLineItemIdBlacklisted = GooglePublisherTag.isLineItemIdBlacklisted(slot, lineItemBlacklist);
    const isRefreshable = slot.getTargeting('refresh').includes('yes');

    if (!isLineItemIdBlacklisted && isRefreshable && disableCoreWebAdRefresh) {
      clearTimeout(slotTimeouts[slotId]);
      slotTimeouts[slotId] = setTimeout(() => {
        store.setState((prev) => ({
          ...prev,
          [slotId]: {
            ...prev[slotId],
            timedOut: true,
          },
        }));
      }, refreshTime);
    }
  };

  // The withIAS function - using ref to avoid this being re-rendered.
  const withIasRef = useRef(null);
  useEffect(() => {
    if (!withIasRef.current) {
      withIasRef.current = withIAS(iasPubId);
    }
  }, [iasPubId]);

  // The skip link id - using ref to avoid this being re-rendered.
  const skipId = useRef(null);

  useEffect(() => {
    if (isSkippable) {
      skipId.current = `skip-to-div-${uniqid()}`;
    }
    // Only create the slot if it doesn't have targetViewport or if the targetViewport matches the activeViewport.
    if (startAds && (mergedConfig.adUnitPath) && (!targetViewport || targetViewport === activeViewport)) {
      // If the slot isn't defined, create it.
      if (!GooglePublisherTag.isSlotDefined(id) && divId === id) {
        GooglePublisherTag.createSlot({
          id,
          mergedConfig,
          lineItemBlacklist,
          disableCoreWebAdRefresh,
          refreshTime,
          firstRun,
          collapseEmptyDiv,
          breakpoints,
          outOfPage,
          headerBidding,
          immediateLoadAdSlots,
          onSlotRenderEnded,
          onSlotLoad,
          onSlotRequested,
          onSlotVisibilityChanged,
          onSlotResponseReceived,
          onImpressionViewable,
          withIas: withIasRef.current,
        });

        if (firstRun) {
          firstRun = false;
        }
      }
    }
    // Clear the timeout on unmount.
    return () => {
      clearTimeout(slotTimeouts[id]);
    };
  }, [activeViewport, mergedConfig.adUnitPath, startAds]);

  const adRef = useRef(null);
  const observer = useRef(null);

  useEffect(() => {
    // Don't run this until the slot is actually defined, i.e. the googletag cmd queue has run.
    if (isDefined === true) {
      if (isInitialFetched === true) {
        store.setState((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            initialFetched: false,
            timedOut: false,
          },
        }));
      } else {
        const gptSlot = GooglePublisherTag.getSlot(id);
        if (gptSlot && Object.keys(gptSlot).length && isTimedOut && adRef.current) {
          observer.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const { googletag } = window;
                const fetchBids = () => {
                  GooglePublisherTag.fetchBids({
                    slotId: entry.target.id,
                    displaySlot: () => googletag.pubads().refresh([GooglePublisherTag.getSlot(entry.target.id)]),
                    size: mergedConfig.slotSize,
                    mapping: mergedConfig.sizeMapping,
                    headerBidding,
                  });
                };
                // Only run the withIAS once per slot.
                if (!isDisplayed) {
                  withIasRef.current({
                    display: () => fetchBids(),
                    iasPetTimeout: 2000,
                  });
                } else {
                  fetchBids();
                }
                if (isDisplayed && !gptSlot.getTargeting('refreshed').includes('true')) {
                  gptSlot.setTargeting('refreshed', true);
                }
                observer.current.unobserve(entry.target);
                store.setState((prev) => ({
                  ...prev,
                  [id]: {
                    ...prev[id],
                    timedOut: false,
                    displayed: true,
                  },
                }));
              }
            });
          }, {
            root: null,
            // Root margin is how we control when the intersect is triggered.
            rootMargin: '65%',
            threshold: 0,
          });
          observer.current.observe(adRef.current);
        }
      }
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [isDefined, isTimedOut, activeViewport]);

  // If this is an OOP slot, return a plain div with an ID.
  if (outOfPage) return <div id={id} />;

  // Get the maximum height to reserve for the ad base on sizeMapping and slotSize.
  let maxAdsBlockHeightToReserve = 'unset';
  if (isSpaceReserved && !activeViewport) {
    maxAdsBlockHeightToReserve = GooglePublisherTag.getReservedHeight(mergedConfig.slotSize, mergedConfig.sizeMapping);
  }

  // This is the initial max height to reserve for the ad. If this changes when the viewport/activeViewport
  // becomes available, we need to re-render the ad to apply the new height.
  const [reservedHeight, setReservedHeight] = useState(maxAdsBlockHeightToReserve);

  useEffect(() => {
    if (isSpaceReserved && activeViewport && divId !== '') {
      const sizes = GooglePublisherTag.getActiveSizeMapping(mergedConfig.slotSize, mergedConfig.sizeMapping);
      const reducer = (maxHeight, currentSize) => {
        if (Array.isArray(currentSize)) {
          const currentSizeHeight = currentSize[1];
          return maxHeight < currentSizeHeight ? currentSizeHeight : maxHeight;
        }
        return maxHeight;
      };
      maxAdsBlockHeightToReserve = sizes.reduce(reducer, 0);
      if (maxAdsBlockHeightToReserve === 0) {
        maxAdsBlockHeightToReserve = 'unset';
      }
      // Set the new max height and rerender the component.
      if (maxAdsBlockHeightToReserve !== reservedHeight) {
        setReservedHeight(maxAdsBlockHeightToReserve);
      }
    }
  }, [activeViewport]);

  return (isPageImpression ? (
    <div
      className={classNames({
        ...extraClassNames,
        [`${cssPrefix}__impression-ad`]: true,
      })}
    >
      <div
        id={divId}
      />
    </div>
  ) : (
    <div
      className={classNames({
        [`${cssPrefix}__ad`]: loadStyles,
        '-full-width': isFullWidth,
        '-mobile-sticky': isMobileSticky,
        '-sidebar-ad': isSidebar,
        '-native-ad': isNative,
        '-no-background': isNoBackground,
        '-empty-ad-slot': (typeof isVisible === 'boolean' ? isVisible === false : true),
        '-rendered-ad-slot': (typeof isRendered === 'boolean' ? isRendered === true : false),
        '-is-space-reserved': isSpaceReserved,
        ...extraClassNames,
      })}
    >
      {isSkippable && (
        <Link
          extraClassNames={{
            '-skip-advert': true,
            '-right-side-skip': isRightSkip,
          }}
          href={`#${skipId.current}`}
        >
          Skip advert
        </Link>
      )}

      <div
        className={classNames({
          [`${cssPrefix}__ad--inner`]: loadStyles,
        })}
      >
        {label && (
          <div className={`${cssPrefix}__ad--label`}>
            {label}
          </div>
        )}
        <div
          id={divId}
          style={{ height: reservedHeight }}
          ref={adRef}
        />
        {isSkippable && (<div id={skipId.current} className="skip-to-div" />)}
      </div>
    </div>
  ));
};

Ad.propTypes = {
  adUnitPath: PropTypes.string,
  collapseEmptyDiv: PropTypes.bool,
  context: PropTypes.shape(),
  extraClassNames: PropTypes.shape(),
  iasPubId: PropTypes.string,
  id: PropTypes.string,
  isFullWidth: PropTypes.bool,
  isMobileSticky: PropTypes.bool,
  isNative: PropTypes.bool,
  isNoBackground: PropTypes.bool,
  isPageImpression: PropTypes.bool,
  isRightSkip: PropTypes.bool,
  isSidebar: PropTypes.bool,
  isSkippable: PropTypes.bool,
  isSpaceReserved: PropTypes.bool,
  label: PropTypes.string,
  loadStyles: PropTypes.bool,
  outOfPage: PropTypes.bool,
  sizeMapping: PropTypes.arrayOf(
    PropTypes.shape({
      viewport: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
      slot: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    }),
  ),
  slotSize: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  targeting: PropTypes.shape(),
  targetViewport: PropTypes.string,
  type: PropTypes.string,
};

Ad.defaultProps = {
  adUnitPath: '',
  collapseEmptyDiv: true,
  context: {},
  extraClassNames: {},
  iasPubId: '931015',
  id: '',
  isFullWidth: false,
  isMobileSticky: false,
  isNative: false,
  isNoBackground: false,
  isPageImpression: false,
  isRightSkip: false,
  isSidebar: false,
  isSkippable: false,
  isSpaceReserved: false,
  label: 'Advertisement',
  loadStyles: true,
  outOfPage: false,
  sizeMapping: [],
  slotSize: [],
  targeting: {},
  targetViewport: undefined,
  type: '',
};

export default compose(withErrorBoundary, withPolaris, withSticky)(Ad);
